import type { GlobalTokens } from "@acto/web-common/theme";
import { styled } from "@pigment-css/react";
import { RadioGroup } from "radix-ui";
import { pressInteraction } from "@acto/taskpane/field-sales-2/styles/pressInteraction";

export const SheetQuestion = styled("h2")(({ theme }) => ({
  margin: 0,
  padding: `${theme.fieldSalesTwo.spacing[12]} ${theme.fieldSalesTwo.spacing[24]} ${theme.fieldSalesTwo.spacing[8]}`,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[18],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: 1.32,
  color: theme.fieldSalesTwo.colors.neutral[900],
}));

const listLayout = (theme: GlobalTokens) =>
  ({
    display: "flex",
    flexDirection: "column",
    gap: theme.fieldSalesTwo.spacing[8],
    paddingInline: theme.fieldSalesTwo.spacing[16],
    marginTop: theme.fieldSalesTwo.spacing[8],
  }) as const;

export const OptionsList = styled(RadioGroup.Root)(({ theme }) => listLayout(theme));

/** Layout twin of {@link OptionsList} without radio-group semantics: no checked state, no arrow-key navigation. */
export const OptionsGroup = styled("div")(({ theme }) => listLayout(theme));

const optionRow = (theme: GlobalTokens) =>
  ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.fieldSalesTwo.spacing[8],
    height: theme.fieldSalesTwo.spacing[48],
    paddingInline: theme.fieldSalesTwo.spacing[20],
    boxSizing: "border-box",
    background: theme.fieldSalesTwo.colors.surface.glass,
    border: `${theme.strokeWidth.thin} solid ${theme.fieldSalesTwo.colors.surface.glassBorder}`,
    borderRadius: theme.fieldSalesTwo.radius[38],
    fontFamily: theme.fieldSalesTwo.font.family,
    fontSize: theme.fieldSalesTwo.font.size[16],
    fontWeight: theme.fieldSalesTwo.font.weight[500],
    lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
    color: theme.fieldSalesTwo.colors.neutral[900],
    textAlign: "left",
    cursor: "pointer",
    WebkitTapHighlightColor: "transparent",
    transition: `background-color ${theme.fieldSalesTwo.motion.duration.fast} ${theme.fieldSalesTwo.motion.easing.standard}, transform ${theme.fieldSalesTwo.motion.duration.fast} ${theme.fieldSalesTwo.motion.easing.standard}`,
    ...pressInteraction(theme),
    "&:focus-visible": {
      outline: `${theme.strokeWidth.regular} solid ${theme.fieldSalesTwo.colors.neutral[700]}`,
      outlineOffset: `-${theme.strokeWidth.regular}`,
    },
    "&:disabled": {
      opacity: 0.4,
      pointerEvents: "none",
    },
  }) as const;

/** Option row; checked state and keyboard navigation come from the enclosing {@link OptionsList}. */
export const OptionButton = styled(RadioGroup.Item)(({ theme }) => optionRow(theme));

export const OptionRow = styled("button")(({ theme }) => optionRow(theme));

/** Radio indicator; fills when checked. Must be a direct child of {@link OptionButton}. */
export const RadioCircle = styled("span")(({ theme }) => ({
  flexShrink: 0,
  width: "22px",
  height: "22px",
  boxSizing: "border-box",
  borderRadius: theme.fieldSalesTwo.radius.circular,
  border: `${theme.strokeWidth.regular} solid ${theme.fieldSalesTwo.colors.neutral[300]}`,
  transition: "border-width 120ms ease, border-color 120ms ease",
  '[data-state="checked"] > &': {
    borderWidth: "7px",
    borderColor: theme.fieldSalesTwo.colors.accent.action,
  },
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none",
  },
}));

/** Checkbox indicator. Must be a direct child of a Radix `Checkbox.Root`, whose `data-state` drives it. */
export const CheckboxSquare = styled("span")(({ theme }) => ({
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "22px",
  height: "22px",
  boxSizing: "border-box",
  borderRadius: theme.fieldSalesTwo.radius["8"],
  border: `1.5px solid ${theme.fieldSalesTwo.colors.neutral[500]}`,
  transition: "background-color 120ms ease, border-color 120ms ease",
  "& svg": { opacity: 0, transition: "opacity 120ms ease" },
  '[data-state="checked"] > &': {
    background: theme.fieldSalesTwo.colors.accent.action,
    borderColor: theme.fieldSalesTwo.colors.accent.action,
    color: theme.fieldSalesTwo.colors.neutral[50],
    "& svg": { opacity: 1 },
  },
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none",
    "& svg": { transition: "none" },
  },
}));
